import React from 'react';
import { Github, ExternalLink } from 'lucide-react';
import type { Project } from '../types/Project';

interface ProjectCardProps {
  project: Project;
  featured?: boolean;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, featured = false }) => {
  const handleCardClick = () => {
    if (project.links.live && project.links.live !== "/") {
      window.open(project.links.live, '_blank', 'noopener noreferrer');
    }
  };

  if (featured) {
    const visibleTags = project.tags.slice(0, 6);
    const hiddenTagCount = project.tags.length - 6;

    return (
      <article
        id={`project-${project.id}`}
        className={`dev-card group overflow-hidden flex flex-col ${
          project.links.live && project.links.live !== "/" ? 'cursor-pointer' : ''
        }`}
        onClick={handleCardClick}
      >
        {/* Image stage, height-capped so the whole card fits a laptop
            viewport. A flat bg-dev-bg-light fill plus a faint dot grid reads
            as a deliberate backdrop; the screenshot itself sits inside the
            padding at object-contain (sized to the image, not the box, so
            the ring/shadow frame hugs it) — never cropped. Above the fold
            and almost certainly the LCP element, so it stays eager and
            jumps the queue ahead of the lazy cards below. */}
        <div className="w-full aspect-[16/9] lg:aspect-auto lg:h-[min(52vh,480px)] bg-dev-bg-light dot-grid-stage overflow-hidden border-b border-dev-border flex items-center justify-center p-5 lg:p-7">
          <img
            src={project.image}
            alt={project.title}
            className="project-frame w-auto h-full max-w-full object-contain rounded-lg ring-1 ring-white/10 shadow-2xl transition-all duration-300 dark:group-hover:brightness-90"
            draggable="false"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </div>

        <div className="p-5 lg:p-6">
          <h3 className="text-xl lg:text-2xl font-bold font-mono text-dev-text group-hover:text-dev-accent transition-colors mb-2">
            {project.title}
          </h3>

          <p className="text-dev-text-muted text-sm xl:text-[15px] leading-relaxed max-w-[68ch]">
            {project.description}
          </p>

          <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
            <div className="flex flex-wrap gap-1.5">
              {visibleTags.map((tag, index) => (
                <span
                  key={index}
                  className="text-[11px] font-mono px-2 py-0.5 rounded border border-dev-border text-dev-text-dimmed"
                >
                  {tag}
                </span>
              ))}
              {hiddenTagCount > 0 && (
                <span className="text-[11px] font-mono px-2 py-0.5 rounded border border-dev-border text-dev-text-dimmed">
                  +{hiddenTagCount}
                </span>
              )}
            </div>

            <div className="flex gap-2" onClick={e => e.stopPropagation()}>
              {project.links.github && (
                <a
                  href={project.links.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-xs md:text-sm border border-dev-border text-dev-text-muted hover:border-dev-accent hover:text-dev-accent transition-all duration-300"
                  aria-label="View GitHub Repository"
                >
                  <Github size={14} />
                  <span>Code</span>
                </a>
              )}
              {project.links.live && project.links.live !== "/" && (
                <a
                  href={project.links.live}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-xs md:text-sm bg-dev-accent text-black hover:brightness-110 transition-all duration-300"
                  aria-label="View Live Demo"
                >
                  <ExternalLink size={14} />
                  <span>Live</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </article>
    );
  }

  // Regular card (non-featured)
  return (
    <article
      id={`project-${project.id}`}
      className={`dev-card group overflow-hidden h-full flex flex-col ${
        project.links.live && project.links.live !== "/" ? 'cursor-pointer' : ''
      }`}
      onClick={handleCardClick}
    >
      <div className="flex-1 flex flex-col">
        {/* Image stage — same dot-grid + framed-screenshot treatment as the
            featured card, so demo screenshots are never cropped here either. */}
        <div className="w-full aspect-[16/9] bg-dev-bg-light dot-grid-stage overflow-hidden flex items-center justify-center p-4">
          {/* Grid cards sit below the featured one, so they wait rather than
              competing with it for bandwidth on first paint. */}
          <img
            src={project.image}
            alt={project.title}
            className="project-frame w-auto h-full max-w-full object-contain rounded-md ring-1 ring-white/10 shadow-lg"
            draggable="false"
            loading="lazy"
            decoding="async"
          />
        </div>

        {/* Content */}
  <div className="flex-1 flex flex-col p-4 md:p-5 lg:p-6">
          <h3 className="text-lg md:text-xl font-bold font-mono mb-2 md:mb-3 text-dev-text group-hover:text-dev-accent transition-colors">
            {project.title}
          </h3>
          <p className="text-dev-text-muted text-sm md:text-sm mb-3 md:mb-4">
            {project.description}
          </p>

          {/* Tech Stack */}
          <div className="flex flex-wrap gap-1.5 mb-3 md:mb-4">
            {project.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="text-[11px] font-mono px-2 py-0.5 rounded border border-dev-border text-dev-text-dimmed"
              >
                {tag}
              </span>
            ))}
            {project.tags.length > 3 && (
              <span className="text-[11px] font-mono px-2 py-0.5 rounded border border-dev-border text-dev-text-dimmed">
                +{project.tags.length - 3}
              </span>
            )}
          </div>

          {/* Links */}
          <div className="flex gap-3 mt-auto" onClick={e => e.stopPropagation()}>
            {project.links.github && (
              <a
                href={project.links.github}
                target="_blank"
                rel="noopener noreferrer"
                className="text-dev-text-muted hover:text-dev-accent transition-colors"
                aria-label="View GitHub Repository"
              >
                <Github size={20} />
              </a>
            )}
            {project.links.live && project.links.live !== "/" && (
              <a
                href={project.links.live}
                target="_blank"
                rel="noopener noreferrer"
                className="text-dev-text-muted hover:text-dev-accent transition-colors"
                aria-label="View Live Demo"
              >
                <ExternalLink size={20} />
              </a>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default ProjectCard;
